import { useEffect, useState } from 'react';
import Button from '@/components/Button';
import ImageUploadForm, { ProgressCallback } from '@/components/ImageUploadForm';
import PriceInput from '@/components/PriceInput';
import ProductCategorySelectBox from '@/components/ProductCategorySelectBox';
import SupplierSelectBox from '@/components/SupplierSelectBox';
import ToggleSwitch from '@/components/ToggleSwitch';
import { useSettings } from '@/contexts/SettingsContext';
import { Image } from '@/models/Image';
import NetContentUnitSelectBox, {
  NetContentUnitItem,
} from '@/modules/product/components/NetContentUnitSelectBox';
import ProductCategory from '@/modules/product/models/ProductCategory';
import Sku, { SkuCreate, SkuUpdate } from '@/modules/product/models/Sku';
import Supplier from '@/modules/product/models/Supplier';
import { ProductCategoryListPath } from '@/modules/product/paths';
import { withLoading, withMinimumDelay } from '@/utils/async';
import { getCurrencySymbol } from '@/utils/currency';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

interface ProductFormProps {
  sku?: Sku;
  skuId?: string;
  onSubmit: (product: SkuCreate | SkuUpdate) => Promise<void>;
  onUploadImage?: (file: File, index: number, callback: ProgressCallback) => Promise<Image>;
  onDeleteImage?: (index: number) => Promise<void>;
  supplier?: Supplier | null;
  supplierId?: string;
  showSupplier?: boolean;
  showSupplierSelect?: boolean;
  selectSupplierCreatedByStoreId?: string;
}

const ProductForm = ({
  sku,
  skuId,
  onSubmit,
  onUploadImage,
  onDeleteImage,
  supplier: supplierProp,
  supplierId: supplierIdProp,
  showSupplier = true,
  showSupplierSelect = false,
  selectSupplierCreatedByStoreId,
}: ProductFormProps) => {
  const [loading, setLoading] = useState(false);

  const { currencyCode: defaultCurrencyCode } = useSettings();

  const supplier = supplierProp ? supplierProp : sku?.supplier;
  const defaultSupplierId = supplier ? supplier.id : supplierIdProp;
  const currencyCode = supplier?.currencyCode || defaultCurrencyCode;
  const currencySymbol = currencyCode && getCurrencySymbol(currencyCode);
  const currentPrice = sku?.prices?.find((price) => price.price.currencyCode === currencyCode);
  const price = { price: currentPrice?.price || { amount: 0, currencyCode } };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SkuCreate | SkuUpdate>({
    defaultValues: sku
      ? { ...sku, price }
      : {
          id: skuId,
          status: 'ACTIVE',
          supplierId: defaultSupplierId,
          price,
        },
  });

  const status = watch('status');
  const productCategory = watch('product.productCategory');
  const priceAmount = watch('price.price.amount') || '';
  const supplierId = watch('supplierId') || '';
  const netContentUnit = watch('netContent.unit') || '';

  const handleStatusChange = (value: boolean) => {
    setValue('status', value ? 'ACTIVE' : 'INACTIVE');
  };

  const handleProductCategoryChange = (value: ProductCategory | null) => {
    setValue('product.productCategoryId', value ? value.id : undefined);
    setValue('product.productCategory', value ? value : undefined);
  };

  const handleSupplierChange = (value: Supplier | null) => {
    setValue('supplierId', value ? value.id : '');
  };

  const handleNetContentUnitChange = (value: NetContentUnitItem | null) => {
    setValue('netContent.unit', value ? value.id : '');
  };

  const handlePriceChange = (amount: number) => {
    setValue('price.price.amount', amount);
    setValue('price.price.currencyCode', currencyCode || '');
  };

  const handleUploadImage = async (
    file: File,
    index: number,
    callback: ProgressCallback,
  ): Promise<Image> => {
    if (!onUploadImage) throw new Error('onUploadImage is not defined');

    setLoading(true);
    try {
      return await onUploadImage(file, index, callback);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    register('product.productCategory', {
      required: 'Product category is required',
    });
    register('price.price.amount', {
      required: 'Price is required and must be greater than 0',
      min: {
        value: 0.01,
        message: 'Price is required and must be greater than 0',
      },
    });
  }, [register]);

  const onSubmitForm = withLoading(withMinimumDelay(onSubmit, 500), setLoading);

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
        {showSupplierSelect && (
          <div className="lg:col-span-2">
            <SupplierSelectBox
              value={supplierId || null}
              onSelect={handleSupplierChange}
              createdByStoreId={selectSupplierCreatedByStoreId}
            />
          </div>
        )}

        {showSupplier && (
          <div className="lg:col-span-2">
            <label htmlFor="supplier" className="block font-medium text-gray-700">
              Supplier:
            </label>
            <input
              type="text"
              id="supplier"
              className="mt-1 p-2 rounded-md w-full read-only focus:outline-none"
              value={supplier?.name}
              readOnly
            />
          </div>
        )}

        <div className="lg:col-span-2">
          <label htmlFor="name" className="block font-medium text-gray-700">
            Name:
          </label>
          <input
            type="text"
            id="name"
            {...register('name', { required: 'Name is required' })}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          <p className="text-sm text-gray-500 mt-1">
            Enter a descriptive name for the product variant.
          </p>
          {errors.name && <span className="text-red-500">{errors.name.message}</span>}
        </div>

        <div className="lg:col-span-2">
          <label htmlFor="description" className="block font-medium text-gray-700">
            Description:
          </label>
          <textarea
            id="description"
            {...register('description')}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          <p className="text-sm text-gray-500 mt-1">
            Provide a short description or details about this product variant.
          </p>
          {errors.description && <span className="text-red-500">{errors.description.message}</span>}
        </div>

        <div className="lg:col-span-2">
          <label htmlFor="externalCode" className="block font-medium text-gray-700">
            Code:
          </label>
          <input
            type="text"
            id="externalCode"
            {...register('externalCode', { required: 'Code is required' })}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          <p className="text-sm text-gray-500 mt-1">
            Enter a unique code to identify this product variant.
          </p>
          {errors.externalCode && (
            <span className="text-red-500">{errors.externalCode.message}</span>
          )}
        </div>

        <div className="lg:col-span-2">
          <ProductCategorySelectBox
            value={productCategory || sku?.product?.productCategoryId || null}
            supplierId={supplierId}
            onSelect={handleProductCategoryChange}
          />
          <p className="mt-1 text-sm text-gray-500">
            Select a product category. Click{' '}
            <Link
              to={ProductCategoryListPath}
              className="text-blue-500 underline hover:text-blue-400 hover:no-underline"
            >
              here
            </Link>{' '}
            to manage product categories.
          </p>
          {errors.product?.productCategory && (
            <span className="text-red-500">{errors.product?.productCategory.message}</span>
          )}
        </div>

        <div className="lg:col-span-2">
          {onUploadImage && onDeleteImage && (
            <ImageUploadForm
              value={sku?.images}
              onUpload={handleUploadImage}
              onDelete={onDeleteImage}
              autoUpload
            />
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
            <div>
              <label htmlFor="quantity" className="block font-medium text-gray-700">
                Units per Case:
              </label>
              <input
                type="number"
                id="quantity"
                {...register('quantity', {
                  valueAsNumber: true,
                  required: 'Units per Case is required',
                })}
                className="mt-1 p-2 border border-gray-300 rounded-md w-full"
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter the number of items per case (e.g., 8 items per case).
              </p>
              {errors.quantity && <span className="text-red-500">{errors.quantity.message}</span>}
            </div>

            <div>
              <label htmlFor="moq" className="block font-medium text-gray-700">
                Minimum Order Quantity (MOQ):
              </label>
              <input
                type="number"
                id="moq"
                {...register('moq', {
                  valueAsNumber: true,
                  required: 'Minimum Order Quantity is required',
                })}
                className="mt-1 p-2 border border-gray-300 rounded-md w-full"
              />
              <p className="text-sm text-gray-500 mt-1">Enter the minimum order quantity.</p>
              {errors.moq && <span className="text-red-500">{errors.moq.message}</span>}
            </div>

            <div>
              <label htmlFor="netContent" className="block font-medium text-gray-700">
                Net Content:
              </label>

              <div className="mt-1 grid grid-cols-[minmax(0,1fr)_minmax(7rem,1fr)] gap-2 sm:max-w-sm">
                <input
                  type="number"
                  id="netContent"
                  step="any"
                  {...register('netContent.value', { valueAsNumber: true })}
                  className="min-w-0 p-2 border border-gray-300 rounded-md"
                />

                <div className="min-w-0">
                  <NetContentUnitSelectBox
                    hideLabel
                    value={netContentUnit}
                    onSelect={handleNetContentUnitChange}
                    name="netContent.unit"
                    placeholder="Unit"
                  />
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-1">
                Specify the net content and its unit (e.g., 500 g, 250 ml).
              </p>

              {errors.netContent?.value && (
                <span className="text-red-500">{errors.netContent.value.message}</span>
              )}
            </div>
          </div>
        </div>

        {currencyCode && (
          <div>
            <label htmlFor="price" className="block font-medium text-gray-700 mb-1">
              Price ({currencySymbol}):
            </label>
            <PriceInput
              value={priceAmount}
              currencyCode={currencyCode}
              onChange={handlePriceChange}
            />
            {errors.price?.price?.amount && (
              <span className="text-red-500">{errors.price.price.amount.message}</span>
            )}
          </div>
        )}

        <div>
          <div className="flex min-w-0 flex-wrap items-end gap-x-2">
            <ToggleSwitch
              checked={status === 'ACTIVE'}
              onChange={handleStatusChange}
              label={<label className="block font-medium text-gray-700 mb-2">Status:</label>}
            />
            <div className="mb-1 min-w-0">{status === 'ACTIVE' ? 'Available' : 'Unavailable'}</div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <fieldset className="border border-gray-300 rounded-md p-4">
            <legend className="text-lg font-medium text-gray-700">Dimensions</legend>
            <div className="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-3">
              <div>
                <label htmlFor="length" className="block text-sm font-medium text-gray-700">
                  Length (cm):
                </label>
                <input
                  type="number"
                  id="length"
                  step="any"
                  {...register('dimensions.length', { valueAsNumber: true })}
                  className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                />
                {errors.dimensions?.length && (
                  <span className="text-red-500">{errors.dimensions.length.message}</span>
                )}
              </div>

              <div>
                <label htmlFor="width" className="block text-sm font-medium text-gray-700">
                  Width (cm):
                </label>
                <input
                  type="number"
                  id="width"
                  step="any"
                  {...register('dimensions.width', { valueAsNumber: true })}
                  className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                />
                {errors.dimensions?.width && (
                  <span className="text-red-500">{errors.dimensions.width.message}</span>
                )}
              </div>

              <div>
                <label htmlFor="height" className="block text-sm font-medium text-gray-700">
                  Height (cm):
                </label>
                <input
                  type="number"
                  id="height"
                  step="any"
                  {...register('dimensions.height', { valueAsNumber: true })}
                  className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                />
                {errors.dimensions?.height && (
                  <span className="text-red-500">{errors.dimensions.height.message}</span>
                )}
              </div>
            </div>
          </fieldset>
          <input type="hidden" value="cm" {...register('dimensions.unit')} />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loadingIconClassName="h-5 w-5 mr-1"
          variant="primary"
          loading={loading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
